import React, { useState, useEffect } from 'react';
import classnames from 'classnames';
import {connect} from 'react-redux';
import {FormattedMessage, FormattedHTMLMessage} from 'react-intl';
import {
  addAllProducts,
  loadProductCart,
  updateProductCart
} from '@ignitesales/neuro-application/src/actions/product-action-creators';
import {actionCompleted, actionStarted} from "@ignitesales/neuro-application/src/actions/question-action-creators";
import { goToNextPage } from "@ignitesales/neuro-application/src/actions/navigation-action-creators";
import { flatProductList } from "@ignitesales/neuro-application/src/util/productUtil";
import Product from '../Product';
import ProductCart from '../ProductCart';
import ProductCartButton from "../ProductCartButton";
import MerlinIcon from "../merlin/MerlinIcon";
import Footer from "../Footer";
import GuideAssistant, {ACTIONS} from "../../refactor/guideAssistant/GuideAssistant";
import summaryPageSteps from "../../utils/guideAssistantSteps/summaryPageSteps";
import {clearNotifications} from "../Notifications";
import AnswersList from "../AnswersList";
import * as analytics from  "../../utils/analytics";

function SummaryPage(props) {
  const {
    addAllProducts,
    addToCartClicked,
    answers,
    answersWithPageInfo,
    classname,
    loadProducts,
    previousPageClicked,
    productApplyClicked,
    productDetailsClicked,
    productCart,
    products,
    removeFromCartClicked,
    user
  } = props;

  useEffect(() => {
    loadProducts();
  }, []);

  const cssBEM = 'summaryPage';
  const classes = classnames(cssBEM, classname);
  const productsRecommendedList = flatProductList(products);
  const productsInCart = productsRecommendedList.filter(p => (productCart || new Set()).has(p.id.toString()));
  const productsNotInCart = productsRecommendedList.filter(p => !(productCart || new Set()).has(p.id.toString()));
  const [ showCart, setShowCart ] = useState(false);
  const [ sticky, setSticky ] = useState(false);
  const [ assistantProps, setAssistantProps ] = useState({});

  useEffect(() => {
    const pageElem = document.getElementsByClassName(cssBEM)[0];
    pageElem.addEventListener('scroll', stickyElementScrollHandler);
    return () => {
      pageElem.removeEventListener('scroll', stickyElementScrollHandler);
    };
  }, []);

  const onGuideAssistantChange = data => {
    const { action } = data;
    if( (action === ACTIONS.STOP || action === ACTIONS.RESET) ) {
      setAssistantProps({
        ...assistantProps,
        run: false
      })
    }
  };

  const stickyElementScrollHandler = () => {
    const threshold = 300;
    const position = document.getElementsByClassName(cssBEM)[0].scrollTop;
    setSticky(position >= threshold);
  };

  const onAddAllProducts = () => {
    addAllProducts(productsRecommendedList);
  };

  const renderProduct = product => {
    const isInTheCart = (productCart || new Set([])).has(product.id.toString());
    const addToCart = () => {
      productApplyClicked({ productId:product.id, completed:false });
      addToCartClicked(productCart, product.id);
    };
    const removeFromCart = () => {
      removeFromCartClicked(productCart, product.id);
    };
    return <Product
      isSelected={isInTheCart}
      key={ product.id }
      onAddCartClick={addToCart}
      onRemoveCartClick={removeFromCart}
      onProductDetailsClick={productDetailsClicked}
      product={product}
    />;
  };

  return <div className={classes}>
    <div className="container">
      <div className={`${cssBEM}__section merlin-section`}>
        <MerlinIcon id='summary-merlin' pulseForever={true} onClick={ () => setAssistantProps({ ...assistantProps, run: true }) } />
        <div className="merlin-section__blurb">
          <FormattedMessage id="summary.subtitle" tagName="h6" />
          <h2 className={`${cssBEM}__section-title`}><FormattedMessage id="summary.title"/></h2>
          <p className={`${cssBEM}__section-subtext`}><FormattedMessage id="summary.subtext" /></p>
          <div>
            <button className="btn back-btn" onClick={onAddAllProducts} disabled={productsNotInCart.length === 0}>
              <i className="fa fa-plus-circle"/>
              <FormattedMessage id='summary.addAllProducts'/>
            </button>
            <ProductCartButton
              isSticky={sticky}
              onClick={() => setShowCart(true)}
              cartCount={productsInCart.length}
            />
          </div>
        </div>
      </div>

      <div className={`${cssBEM}__section`}>
        <div className={`${cssBEM}__products`}>
          { productsRecommendedList.map(p => renderProduct(p)) }
        </div>
      </div>
    </div>

    <div className={`${cssBEM}__answers`}>
      <div className="container">
        <h2 className={`${cssBEM}__section-title`}><FormattedMessage id="answers.title"/></h2>
        <p className={`${cssBEM}__section-subtext`}>
          <FormattedMessage id="answers.subtext" values={{
            returnLink: <span onClick={previousPageClicked}><FormattedMessage id="answers.subtext.previousPage" /></span>
          }}/>
        </p>
        <AnswersList answers={answersWithPageInfo} user={user} />
      </div>
    </div>

    <div className={`${cssBEM}__disclosures`}>
      <div className="container">
        <p className={`${cssBEM}__section-subtext`}><FormattedHTMLMessage id="disclosures.subtext"/></p>
      </div>
    </div>

    <ProductCart
      answers={ answers }
      isOpen={ showCart }
      onClose={ () => setShowCart(false) }
      onRemoveFromCart={ id => removeFromCartClicked(productCart, id) }
      productApplyClicked={ productApplyClicked }
      selectedProducts={ productsInCart }
    />

    <GuideAssistant
      id="summary"
      steps={ summaryPageSteps }
      delay={1250}
      continuous={true}
      showSkipButton={true}
      enforceCache={true}
      onChange={onGuideAssistantChange}
      floaterProps={{
        disableAnimation: true
      }}
      { ...assistantProps }
    />

    <Footer/>
  </div>;
}

export default connect((state) => {
  const answers = state.answers || {};
  const answersWithPageInfo = state.answersWithPageInfo || {};
  const products = state.products || {};
  const productCart = state.productCart;
  const user = state.user || {};
  return {
    answers,
    answersWithPageInfo,
    products,
    productCart: Array.isArray(productCart) ? new Set(productCart) : productCart,
    user
  };
}, dispatch => {
  return {
    addToCartClicked: (products, productId) => {
      analytics.processEvent({ event: 'addToCart', data: { productId } });

      dispatch(updateProductCart(products, productId));
    },
    removeFromCartClicked: (products, productId) => {
      dispatch(updateProductCart(products, productId, true));
    },
    addAllProducts: (listOfProducts) => {
      analytics.processEvent({ event: 'addAllToCart', data: { listOfProducts } });

      dispatch(addAllProducts(listOfProducts));
    },
    previousPageClicked: () => {
      clearNotifications();
      dispatch(goToNextPage(true));
    },
    loadProducts: () => {
      dispatch(loadProductCart());
    },
    productApplyClicked: ({ productId, completed, url }) => {
      const params = {
        actionName: 'productApplyClicked',
        productId
      };

      if (url === '#!') {
        analytics.processEvent({ event: 'contact', data: {productId} });
      } else if (completed) {
        analytics.processEvent({ event: 'apply', data: {productId, url} });
      }

      dispatch(completed ? actionCompleted(params) : actionStarted(params));
    },
    productDetailsClicked: (productId) => {
      const params = {
        actionName: 'productDetailsClicked',
        productId
      };
      dispatch(actionCompleted(params));
    }
  };
})(SummaryPage);
